import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { useSnackbar } from 'notistack';
import { setLoading } from 'Actions/loading';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { PaginationBar } from 'Shared/PaginationBar';
import { AlertDialog } from 'Shared/AlertDialog';
import http from 'Utils/http';
import { cachedGet } from 'Utils/cachedGet';
import { process } from 'Utils/errorHandler';
import { useCopyText } from 'Utils/clipboard';

const EMPTY_SEARCH = { email: '', api: '', domain: '' };
const EMPTY_INSTANCE = { user: null, domain: '', engineUrl: '' };

const useRequest = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    return useCallback((promise, onSuccess) => {
        dispatch(setLoading(true));
        promise
            .then((res) => onSuccess(res.data))
            .catch((err) => process(err, enqueueSnackbar))
            .finally(() => dispatch(setLoading(false)));
    }, [dispatch, enqueueSnackbar]);
};

const Statistics = () => {
    const request = useRequest();

    const [search, setSearch] = useState(EMPTY_SEARCH);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(DateTime.now());
    const [filter, setFilter] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [records, setRecords] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalCalls, setTotalCalls] = useState(0);
    const [options, setOptions] = useState({ email: [], domain: [] });

    useEffect(() => {
        request(http.get('/admin/usage/instances'), (data) => {
            const instances = data.instances || [];
            setOptions({
                email: [...new Set(instances.map((instance) => instance.user?.email).filter(Boolean))],
                domain: instances.map((instance) => instance.domain),
            });
        });
    }, [request]);

    useEffect(() => {
        const pagination = encodeURIComponent(JSON.stringify({ pageSize: rowsPerPage, currentPage: page }));
        request(http.get(`/admin/usage/stats?pagination=${pagination}&filter=${encodeURIComponent(JSON.stringify(filter))}`), (data) => {
            setRecords(data.records || []);
            setTotalRecords(data.pagination?.totalRecords || 0);
            setTotalCalls(data.total_calls || 0);
        });
    }, [request, filter, page, rowsPerPage]);

    const searchHandler = (event) => {
        event.preventDefault();
        const next = {};
        Object.entries(search).forEach(([key, value]) => {
            if (value.trim()) {
                next[key] = key === 'email' ? value.trim().toLowerCase() : value.trim();
            }
        });
        if (dateFrom) {
            next.from = dateFrom.toISODate();
        }
        if (dateTo) {
            next.to = dateTo.toISODate();
        }
        setPage(0);
        setFilter(next);
    };

    const clearHandler = () => {
        setSearch(EMPTY_SEARCH);
        setDateFrom(null);
        setDateTo(DateTime.now());
        setPage(0);
        setFilter({});
    };

    return (
        <>
            <Box component="form" onSubmit={searchHandler} sx={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <DatePicker
                        label="Date from"
                        value={dateFrom}
                        maxDate={dateTo}
                        onChange={setDateFrom}
                        slotProps={{ textField: { size: 'small' } }}
                    />
                    <DatePicker
                        label="Date to"
                        value={dateTo}
                        maxDate={DateTime.now()}
                        onChange={setDateTo}
                        slotProps={{ textField: { size: 'small' } }}
                    />
                </LocalizationProvider>
                {[['email', 'Email'], ['domain', 'Domain']].map(([key, label]) => (
                    <Autocomplete
                        key={key}
                        options={options[key]}
                        value={search[key] || null}
                        onChange={(e, value) => setSearch({ ...search, [key]: value || '' })}
                        renderInput={(params) => <TextField {...params} label={label} size="small" />}
                        sx={{ minWidth: '220px' }}
                    />
                ))}
                <TextField
                    label="API"
                    size="small"
                    value={search.api}
                    onChange={(e) => setSearch({ ...search, api: e.target.value })}
                />
                <Button variant="contained" type="submit">Search</Button>
                <Button variant="outlined" onClick={clearHandler}>Clear</Button>
                <Typography variant="h4" sx={{ fontWeight: '500', color: '#3B465C' }}>
                    Total calls: {totalCalls}
                </Typography>
            </Box>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">Email</TableCell>
                            <TableCell align="center">API</TableCell>
                            <TableCell align="center">Date</TableCell>
                            <TableCell align="center">Calls</TableCell>
                            <TableCell align="center">Domain</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {records.map((record) => (
                            <TableRow key={`${record.domain}_${record.date}_${record.api}`}>
                                <TableCell align="center">{record.email}</TableCell>
                                <TableCell align="center">{record.api}</TableCell>
                                <TableCell align="center">{record.date}</TableCell>
                                <TableCell align="center">{record.calls}</TableCell>
                                <TableCell align="center">{record.domain}</TableCell>
                            </TableRow>
                        ))}
                        {!records.length && (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ color: '#99a1ad' }}>No usage yet</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <PaginationBar
                count={totalRecords}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                }}
            />
        </>
    );
};

const Instances = () => {
    const request = useRequest();
    const { enqueueSnackbar } = useSnackbar();
    const copy = useCopyText();

    const [instances, setInstances] = useState([]);
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState(null);
    const [secret, setSecret] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(() => {
        request(http.get('/admin/usage/instances'), (data) => setInstances(data.instances || []));
    }, [request]);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        cachedGet('/users')
            .then((res) => setUsers(res.data.users || []))
            .catch((err) => process(err, enqueueSnackbar));
        setForm(EMPTY_INSTANCE);
    };

    const create = () => {
        const body = { user_id: form.user.id, domain: form.domain.trim(), engine_url: form.engineUrl.trim() || null };
        setForm(null);
        request(http.post('/admin/usage/instance/create', body), (data) => {
            setSecret(data.secret);
            load();
        });
    };

    const pull = (instance) => {
        request(http.post(`/admin/usage/instance/${instance.id}/pull`), () => {
            enqueueSnackbar(`Usage of ${instance.domain} has been pulled`, { action: CustomSnackbar, variant: 'success' });
        });
    };

    const remove = () => {
        const instance = deleting;
        setDeleting(null);
        request(http.delete(`/admin/usage/instance/${instance.id}/delete`), () => {
            enqueueSnackbar(`${instance.domain} has been deleted`, { action: CustomSnackbar, variant: 'success' });
            load();
        });
    };

    return (
        <>
            <Box sx={{ display: 'flex', marginBottom: '20px' }}>
                <Button variant="contained" onClick={openCreate} sx={{ marginLeft: 'auto' }}>
                    Create instance
                </Button>
            </Box>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Email</TableCell>
                            <TableCell>Domain</TableCell>
                            <TableCell>Engine URL</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {instances.map((instance) => (
                            <TableRow key={instance.id}>
                                <TableCell>{instance.user?.email}</TableCell>
                                <TableCell>{instance.domain}</TableCell>
                                <TableCell>{instance.engine_url || '-'}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Button onClick={() => pull(instance)} disabled={!instance.engine_url}>Pull</Button>
                                    <Button color="error" onClick={() => setDeleting(instance)}>Delete</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!instances.length && (
                            <TableRow>
                                <TableCell colSpan={4} sx={{ color: '#99a1ad' }}>No instances yet</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={form !== null} onClose={() => setForm(null)} fullWidth maxWidth="sm">
                <DialogTitle>Create instance</DialogTitle>
                {form && (
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingTop: '10px !important' }}>
                        <Autocomplete
                            options={users}
                            getOptionLabel={(user) => user.email}
                            value={form.user}
                            onChange={(e, user) => setForm({ ...form, user })}
                            renderInput={(params) => <TextField {...params} label="User" />}
                        />
                        <TextField label="Domain" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} inputProps={{ maxLength: 255 }} />
                        <TextField label="Engine URL" value={form.engineUrl} onChange={(e) => setForm({ ...form, engineUrl: e.target.value })} inputProps={{ maxLength: 255 }} />
                    </DialogContent>
                )}
                <DialogActions>
                    <Button variant="outlined" onClick={() => setForm(null)}>Close</Button>
                    <Button variant="contained" onClick={create} disabled={!form?.user || !form?.domain.trim()}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={secret !== null} onClose={() => setSecret(null)} fullWidth maxWidth="sm">
                <DialogTitle>Instance secret</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Typography sx={{ color: '#3B465C' }}>
                        Copy the secret now into USAGE_SECRET of the instance. It is shown only once and cannot be recovered.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <TextField label="Secret" value={secret || ''} fullWidth InputProps={{ readOnly: true }} />
                        <Button onClick={() => copy(secret, 'Secret')}>Copy</Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setSecret(null)}>Done</Button>
                </DialogActions>
            </Dialog>

            <AlertDialog
                visible={deleting !== null}
                onClose={() => setDeleting(null)}
                onAgree={remove}
                text={`Delete ${deleting?.domain} together with its usage statistics?`}
            />
        </>
    );
};

const ExcludedHosts = () => {
    const request = useRequest();
    const { enqueueSnackbar } = useSnackbar();

    const [hosts, setHosts] = useState([]);

    useEffect(() => {
        request(http.get('/admin/usage/settings'), (data) => setHosts(data.excluded_hosts || []));
    }, [request]);

    const save = () => {
        request(http.post('/admin/usage/settings', { excluded_hosts: hosts }), (data) => {
            setHosts(data.excluded_hosts || []);
            enqueueSnackbar('Excluded hosts have been saved', { action: CustomSnackbar, variant: 'success' });
        });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={hosts}
                onChange={(e, value) => setHosts(value)}
                renderTags={(value, getTagProps) => value.map((host, index) => (
                    <Chip label={host} {...getTagProps({ index })} key={host} />
                ))}
                renderInput={(params) => <TextField {...params} label="Excluded hosts" placeholder="Type a host and press Enter" />}
            />
            <Box>
                <Button variant="contained" onClick={save}>Save</Button>
            </Box>
        </Box>
    );
};

const UsageStatsPage = () => {
    const [tab, setTab] = useState('statistics');

    return (
        <>
            <Typography
                variant="h1"
                sx={{
                    textAlign: 'center',
                    marginBottom: '25px',
                    fontSize: '3rem',
                    fontWeight: 500,
                }}
            >
                Usage Stats
            </Typography>
            <Box component={Paper} sx={{ padding: '20px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <Tabs value={tab} onChange={(e, value) => setTab(value)}>
                        <Tab value="statistics" label="Statistics" />
                        <Tab value="instances" label="Instances" />
                        <Tab value="excluded" label="Excluded hosts" />
                    </Tabs>
                    <Button
                        component="a"
                        href={`${http.defaults.baseURL}/docs/usage`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ marginLeft: 'auto' }}
                    >
                        Help
                    </Button>
                </Box>
                {tab === 'statistics' && <Statistics />}
                {tab === 'instances' && <Instances />}
                {tab === 'excluded' && <ExcludedHosts />}
            </Box>
        </>
    );
};

export default UsageStatsPage;
